import { useUnmount } from 'react-use';
import { devtools } from 'zustand/middleware';

import { createStoreWithReset } from '@/libs/zustand';
import { Registry } from '@/api/domains/registers.types';

type State = {
  isFilterPanelVisible: boolean;
  selectedRegistry: Registry | null;
  isLoadFileModalOpen: boolean;
};

type Actions = {
  setSelectedRegistry: (registry: Registry) => void;
  resetSelectedRegistry: VoidFunction;

  openLoadFileModal: VoidFunction;
  closeLoadFileModal: VoidFunction;

  toggleFiltersPanel: VoidFunction;
};

export type RegistersStore = State & { actions: Actions };

const [useRegistersStore, resetRegistersStore] = createStoreWithReset<RegistersStore>()(
  devtools(
    (set, get) => ({
      isFilterPanelVisible: false,
      selectedRegistry: null,
      isLoadFileModalOpen: false,

      actions: {
        setSelectedRegistry: registry => set({ selectedRegistry: registry }, false, 'setSelectedRegistry'),
        resetSelectedRegistry: () => set({ selectedRegistry: null }, false, 'resetSelectedRegistry'),

        openLoadFileModal: () => set({ isLoadFileModalOpen: true }, false, 'openLoadFileModal'),
        closeLoadFileModal: () => set({ isLoadFileModalOpen: false }, false, 'closeLoadFileModal'),

        toggleFiltersPanel: () =>
          set(
            {
              isFilterPanelVisible: !get().isFilterPanelVisible,
            },
            false,
            'toggleFiltersPanel',
          ),
      },
    }),
    {
      name: 'registers-store',
      enabled: process.env.NODE_ENV === 'development',
    },
  ),
);

export const useResetRegistersStoreOnUnmount = () => useUnmount(resetRegistersStore);

export const useRegistersStoreActions = () => useRegistersStore(state => state.actions);
export const useRegistersStoreIsContextWindowsOpen = () =>
  useRegistersStore(state => ({
    isLoadFileModalOpen: state.isLoadFileModalOpen,
  }));

export const useRegistersStoreIsFilterVisible = () => useRegistersStore(state => state.isFilterPanelVisible);

export const useSelectedRegistry = () => useRegistersStore(state => state.selectedRegistry);

export { resetRegistersStore, useRegistersStore };
